/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../_metronic/layout/core'
import {Link, useLocation} from 'react-router-dom'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

interface QuickActionCard {
  id: string
  title: string
  description: string
  icon: string
  link: string
  color: string
  requiredMenus?: string[] // Menu IDs or names that grant access
}

// Icon mapping for different menu types
const getIconForMenu = (menuName: string, menuLink: string): string => {
  const name = menuName.toLowerCase()
  const link = menuLink.toLowerCase()
  
  if (name.includes('satsangi') && (name.includes('admin') || name.includes('management'))) return 'bi-people'
  if (name.includes('satsangi') && name.includes('review')) return 'bi-clipboard-check'
  if (name.includes('user') && (name.includes('admin') || name.includes('management'))) return 'bi-person-gear'
  if (name.includes('mandir') || name.includes('temple')) return 'bi-building'
  if (name.includes('menu')) return 'bi-list'
  if (name.includes('publication')) return 'bi-journal'
  if (name.includes('notification')) return 'bi-bell'
  if (name.includes('report')) return 'bi-file-earmark-bar-graph'
  if (name.includes('setting')) return 'bi-gear'
  if (name.includes('role') || name.includes('permission')) return 'bi-shield-check'
  
  return 'bi-folder'
}

// Color mapping for different menu types
const getColorForMenu = (index: number): string => {
  const colors = ['primary', 'success', 'info', 'warning', 'danger', 'secondary']
  return colors[index % colors.length]
}

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  const location = useLocation()
  const [userName, setUserName] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [availableActions, setAvailableActions] = useState<QuickActionCard[]>([])

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Get user data
        const userResponse = await axios.get(`${API_URL}admin-user/token`)
        const userData = userResponse.data.data?.[0] || userResponse.data.data
        
        // Use only first name for the welcome message - field name is 'fName'
        const firstName = userData?.fName || 'User'
        setUserName(firstName)
        
        const fullPermissionString = userData?.fullPermission || ''
        // More robust permission parsing - trim each permission and filter out empty strings
        const permissions = fullPermissionString
          .split(',')
          .map((p: string) => p.trim())
          .filter((p: string) => p !== '')

        console.log('[Dashboard] ==============================================')
        console.log('[Dashboard] RAW Full permission string:', JSON.stringify(fullPermissionString))
        console.log('[Dashboard] Parsed permissions array:', JSON.stringify(permissions))
        console.log('[Dashboard] Number of permissions:', permissions.length)
        
        // Check each permission individually
        permissions.forEach((perm: string, idx: number) => {
          console.log(`[Dashboard] Permission ${idx}:`, perm, 'Length:', perm.length)
        })

        // Get all menus
        const menuResponse = await axios.get(`${API_URL}menu/admin-menu/all/`)
        const allMenus = menuResponse.data.data || []

        console.log('[Dashboard] Total menus in system:', allMenus.length)
        console.log('[Dashboard] All menus:', allMenus.map((m: any) => ({
          id: m._id,
          name: m.menu_name,
          link: m.menu_link
        })))

        // Check if user has wildcard permission
        const hasWildcard = permissions.includes('*')

        if (hasWildcard) {
          console.log('[Dashboard] ✓ User has wildcard (*) permission - showing all menus')
        } else {
          console.log('[Dashboard] User does NOT have wildcard permission - filtering by menu IDs')
        }

        // Filter menus based on permissions and convert to action cards
        const accessibleMenus = allMenus.filter((menu: any) => {
          const menuId = menu._id
          const menuName = menu.menu_name
          
          // Grant access to all menus if wildcard
          if (hasWildcard) {
            console.log(`[Dashboard] Checking "${menuName}" (${menuId}): ✓ GRANTED (wildcard)`)
            return true
          }
          
          // Check if user's permission list includes this menu's ID
          const hasAccess = permissions.includes(menuId)
          
          console.log(`[Dashboard] Checking "${menuName}" (${menuId}):`, hasAccess ? '✓ GRANTED' : '✗ DENIED')
          console.log(`[Dashboard]   - Menu ID: "${menuId}"`)
          console.log(`[Dashboard]   - ID in permissions?: ${hasAccess}`)
          console.log(`[Dashboard]   - Exact match check:`, permissions.map((p: string) => p === menuId ? '✓' : '✗'))
          
          return hasAccess
        })

        console.log('[Dashboard] ==============================================')
        console.log('[Dashboard] Accessible menus count:', accessibleMenus.length, 'out of', allMenus.length)

        // Convert accessible menus to quick action cards
        const actions: QuickActionCard[] = accessibleMenus
          .map((menu: any, index: number) => {
            const menuName = menu.menu_name || 'Unknown'
            const menuLink = menu.menu_link || ''
            
            console.log(`[Dashboard] Processing menu: "${menuName}" with link: "${menuLink}"`)
            
            // Filter out parent/container menus that don't have functional links
            // These are typically organizational menus that contain child menus
            // Parent menus usually have:
            // - "Management" in the name (e.g., "Satsangi Management", "User Management")
            // - A simple single-word link (e.g., "satsangi", "user")
            // - OR no link at all / empty link
            
            const hasManagementInName = menuName.toLowerCase().includes('management')
            const hasSimpleLink = menuLink && menuLink.split('/').length === 1
            const hasNoProperLink = !menuLink || menuLink === '#' || menuLink.trim() === ''
            
            const isLikelyParentMenu = hasManagementInName && (hasSimpleLink || hasNoProperLink)
            
            if (isLikelyParentMenu) {
              console.log(`[Dashboard] ⚠️ Filtering out parent/container menu: "${menuName}" (link: "${menuLink}")`)
              console.log(`[Dashboard]   Reason: Management menu with ${hasSimpleLink ? 'simple link' : 'no proper link'}`)
              return null
            }
            
            // Generate a friendly description
            let description = `Access ${menuName.toLowerCase()}`
            if (menuName.toLowerCase().includes('admin') || menuName.toLowerCase().includes('management')) {
              description = `Manage and configure ${menuName.replace(/admin|management/gi, '').trim().toLowerCase()}`
            } else if (menuName.toLowerCase().includes('review')) {
              description = `Review and approve ${menuName.replace(/review/gi, '').trim().toLowerCase()} submissions`
            } else if (menuName.toLowerCase().includes('report')) {
              description = `View and generate ${menuName.replace(/report/gi, '').trim().toLowerCase()} reports`
            }

            // Smart link handling - add proper route segments
            let fullLink = menuLink
            
            // If link is incomplete (e.g., "satsangi", "user", "mandir"), add default /admin/list
            if (menuLink && !menuLink.includes('/')) {
              // Single word menu links should default to admin list
              fullLink = `/${menuLink}/admin/list`
            } else if (menuLink && menuLink.split('/').length === 2 && !menuLink.endsWith('list')) {
              // Two-part links without 'list' should add /list (e.g., "satsangi/admin" -> "satsangi/admin/list")
              fullLink = `/${menuLink}/list`
            } else {
              // Otherwise ensure it starts with /
              fullLink = menuLink.startsWith('/') ? menuLink : `/${menuLink}`
            }

            // Skip menus with invalid or empty links
            if (!menuLink || menuLink === '#' || menuLink.trim() === '') {
              return null
            }

            return {
              id: menu._id,
              title: menuName,
              description: description,
              icon: getIconForMenu(menuName, menuLink),
              link: fullLink,
              color: getColorForMenu(index),
              requiredMenus: [menuName]
            }
          })
          .filter((action: QuickActionCard | null): action is QuickActionCard => action !== null) // Remove null entries

        console.log('[Dashboard] Final cards to display:', actions.length)
        console.log('[Dashboard] Card details:', actions.map(a => ({
          title: a.title,
          link: a.link
        })))

        setAvailableActions(actions)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching user data:', error)
        setLoading(false)
      }
    }

    fetchUserData()

    // Check for error message from navigation state
    if (location.state && (location.state as any).error) {
      setErrorMessage((location.state as any).error)
      // Clear the error after showing it
      setTimeout(() => setErrorMessage(''), 5000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const getColorClasses = (color: string) => {
    return {
      card: `bg-light-${color}`,
      icon: `text-${color}`,
      iconBg: `bg-${color}`,
      hover: `hover-elevate-up`
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>

      {/* Error Message */}
      {errorMessage && (
        <div className='alert alert-danger d-flex align-items-center mb-5' role='alert'>
          <i className='bi bi-exclamation-triangle-fill fs-2x me-4'></i>
          <div>{errorMessage}</div>
        </div>
      )}

      {/* Welcome Section */}
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-6'>
            <div className='flex-grow-1'>
              <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <span className='text-gray-800 fs-2 fw-bold me-1'>
                      Welcome back, {userName}! 👋
                    </span>
                  </div>
                  <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                    <span className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                      <i className='bi bi-calendar3 fs-4 me-1'></i>
                      {new Date().toLocaleDateString('en-GB', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      {loading ? (
        <div className='d-flex justify-content-center align-items-center' style={{minHeight: '300px'}}>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className='mb-5'>
            <h3 className='text-gray-800 fw-bold mb-2'>Quick Actions</h3>
            <p className='text-gray-600 fw-semibold fs-6 mb-4'>
              Access your most used features quickly
            </p>
          </div>

          {availableActions.length > 0 ? (
            <div className='row g-5 g-xl-8'>
              {availableActions.map((action) => {
                const colorClass = getColorClasses(action.color)
                return (
                  <div key={action.id} className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
                    <Link to={action.link} className='text-decoration-none'>
                      <div 
                        className={`card card-xl-stretch ${colorClass.hover} h-100`}
                        style={{transition: 'all 0.3s ease'}}
                      >
                        <div className='card-body d-flex flex-column'>
                          <div className='d-flex align-items-center mb-5'>
                            <div 
                              className={`symbol symbol-50px ${colorClass.iconBg} bg-opacity-10 me-3`}
                              style={{borderRadius: '12px'}}
                            >
                              <span className={`symbol-label ${colorClass.icon}`}>
                                <i className={`bi ${action.icon} fs-2x`}></i>
                              </span>
                            </div>
                          </div>
                          
                          <div className='flex-grow-1'>
                            <h3 className='text-gray-800 fw-bold mb-3 fs-5'>
                              {action.title}
                            </h3>
                            <p className='text-gray-600 fw-semibold fs-7 mb-0'>
                              {action.description}
                            </p>
                          </div>

                          <div className='mt-5'>
                            <span className={`btn btn-sm btn-${action.color} w-100`}>
                              Open <i className='bi bi-arrow-right ms-2'></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className='card'>
              <div className='card-body text-center py-10'>
                <i className='bi bi-inbox fs-3x text-gray-400 mb-5'></i>
                <h3 className='text-gray-800 fw-bold mb-2'>No Quick Actions Available</h3>
                <p className='text-gray-600 fw-semibold fs-6'>
                  Contact your administrator to get access to system features.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export {DashboardWrapper}
